<template>
  <div class="article-wrapper">
    <h1>How can Clavo help you with your teamwork management?</h1>
    <p class="time-and-date">
      Jan 21, 2023 | 3 min read
    </p> 
    <p>
      It can help if you are a manager of a team and your coworkers work in different time slots, or if some of them work remotely, part-time or perhaps in different workplaces from day to day. It can be of assistance in even more complex situations, for example if they work in different time zones.
    </p>
    <div class="photo-w-slogan">
      <figure>
        <img 
          src="@/assets/articles/stephanie_entrepreneur.jpg"
          alt="Stephanie the entrepreneur"
          width="711"
          height="439"
        >
        <figcaption>&copy; 2019 Stephanie Liverani</figcaption>
      </figure>
      <h2>“I couldn’t find out my team's actual working hours.”</h2>
    </div>
    <h3>Why do you need to know when they work?</h3>
    <ul>
      <li>You can plan meetings ahead of time.</li>
      <li>Based on their schedule you can predict that some part of the work will be done in the near future.</li>
      <li>Based on number of hours scheduled you can assume if you can assign some of the work for the current week.</li>
      <li>You can predict how much money will be spent on the work during the current week.</li>
      <li>All of your team members can coordinate their timeline if they do work together on some tasks.</li>
      <li>You will always know if and when you can call your team’s employee/colleague.</li>
    </ul>
    <h3>You can save a lot of time if you are aware when your team members are working.</h3>
    <p>Let’s assume you need to contact some of your team members. How would you know if and when they will be available today? How would you choose a meeting time if you don’t know when they are working?</p>
    <h3>How some companies deal with these problems:</h3>
    <figure>
      <img
        src="@/assets/articles/spreadsheet_work_schedule.jpg"
        alt="Example of messy work schedule done in Excel"
      >
      <figcaption>&copy;2020 Clavo. An example of spreadsheet showing team’s weekly schedule.</figcaption>
    </figure>
    <p>
      They maintain shared spreadsheets - where anyone can edit their work schedule.
      <br>That’s a possible solution, but not very helpful if someone is making many changes.
      <br><br>Some managers ask their team members to write their schedule on company communicators or directly to the manager each day. It’s not very efficient and it’s cumbersome to check.
    </p>
    <h3>How can Clavo fix these issues?</h3>
    <p>
      Clavo will allow team members to create schedules only for the current and following week so that everyone enters fresh data. The free time entries are the exception because vacations can be planned even a few months in advance.
      <br><br>If anyone forgets to fill in their weekly schedule - they will be reminded via email, Slack, Discord and Teams. You will no longer need to chase them up anymore.
    </p>
    <figure>
      <img 
        src="@/assets/articles/notification_channels.jpg"
        alt="Different notification channels available in Clavo"
      >
    </figure>
    <p>
      Each user will get a reminder the day before the deadline (let’s say it’s 10 AM each Monday) and they will be reminded every day until they fill in the schedule.
    </p>
    <figure>
      <img 
        src="@/assets/articles/clavo_dashboard.jpg"
        alt="Dashboard of Clavo with example of members' schedules in different time zones"
      >
    </figure>
    <p>
      The end result is an easily to accessible weekly agenda that improves both awareness and productivity. You know what to expect, without any surpises.
      <strong>Check it out, it’s quite easy and user-friendly!</strong>
    </p>
    <BottomCTA />
  </div>
</template>
<script>
import BottomCTA from '@/components/main_page/BottomCTA.vue'

export default {
  name: 'HowItWorks',
  components:  {
    BottomCTA
  },
  props: {
  }
}
</script>


<style lang="scss" scoped>
.article-wrapper {
    text-align: left;
    max-width: $maxWidth;
    margin: 130px auto 0 auto;
    // padding-right: 120px;
    @include respond-below(md) {
      padding-right: 0;
    }
    h1, h2 {
      margin-bottom: 36px;
    }
    h3 {
      margin-top: 48px;
      margin-bottom: 24px;
    }
    h4 {
      margin-top: 48px;
      margin-bottom: 12px;
    }
    ul, ol {
      @include font-list;
    }
    p {
      @include font-body;
    }
    a {
      @include font-body-link;
    }
    figure {
      margin: 48px 0 48px 0;
      figcaption {
        color: $grayD;
        font-size: 14px;
      }
    }
    .time-and-date {
      color: $grayD;
      margin-bottom: 36px;
    }
    .photo-w-slogan {
      display: flex;
      flex-direction: column;
      figure {
        margin: 48px calc(var(--gutter) * -1);
      }
      figcaption {
        margin: 0 var(--gutter);
      }
      h2 {
        background: white;
        margin-top: -120px;
        margin-bottom: 0;
        padding: 24px;
      }
      @include respond-above(md) {
        display: block;
        position: relative;
        figure {
          img {
            margin-left: -20%;
          }
        }
        h2 {
          margin-top: 0;
          padding: 36px;
          position: absolute;
          width: 70%;
          top: 45%;
          right: 0;
          transform: translateY(-50%);
        }
      }
    }
    .subscription {
      margin-bottom: 48px;
    }
}
</style>